import { useState, useEffect, Suspense, lazy } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Grid,
  Box,
  Typography,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  LinearProgress,
  Alert,
  Skeleton,
  Collapse,
  useTheme,
} from '@mui/material'
import {
  Inventory2,
  Build,
  Assignment,
  Warning,
  CheckCircle,
  Schedule,
  TrendingUp,
  LocalShipping,
  Speed,
  Circle,
  ArrowForward,
  QrCodeScanner,
  ExpandMore,
  ExpandLess,
} from '@mui/icons-material'
import { Doughnut } from 'react-chartjs-2'
import '@utils/chartSetup'
import { PageHeader, StatCard, Card, RadialGauge, LoadingSpinner, StatusChip, PermissionGate } from '@components/common'
import { useAuth } from '@context/AuthContext'
import { useBranding } from '@context/BrandingContext'
import api from '@services/api'
import { formatDateTime } from '@utils/dateUtils'

// Heavy analytics section (Plotly + framer-motion) — lazy-loaded so it stays out
// of the main bundle even though the Dashboard is the landing route.
const AnalyticsSection = lazy(() => import('./analytics/AnalyticsSection'))

const Dashboard = () => {
  const navigate = useNavigate()
  const theme = useTheme()
  const { user } = useAuth()
  const { orgName } = useBranding()
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)
  // Collapsible analytics: expanded by default so the graphs are up front, but the
  // choice is persisted per device so collapsing it sticks. The heavy
  // AnalyticsSection (Plotly + its own API calls) still code-splits out of the main
  // bundle; it mounts as soon as the section is open and stays mounted afterwards.
  const [analyticsOpen, setAnalyticsOpen] = useState(() => {
    try {
      const stored = localStorage.getItem('dashboard_analytics_open')
      return stored === null ? true : stored === 'true'
    } catch {
      return true
    }
  })
  const [analyticsMounted, setAnalyticsMounted] = useState(analyticsOpen)

  const toggleAnalytics = () => {
    setAnalyticsOpen((prev) => {
      const next = !prev
      if (next) setAnalyticsMounted(true)
      try {
        localStorage.setItem('dashboard_analytics_open', String(next))
      } catch {
        // ignore persistence failures (e.g. private mode)
      }
      return next
    })
  }

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await api.get('/common/dashboard/')
        setStats(response.data)
      } catch (error) {
        console.error('Error fetching dashboard stats:', error)
      } finally {
        setLoading(false)
      }
    }
    fetchStats()
  }, [])

  if (loading) {
    return <LoadingSpinner message="Loading dashboard..." />
  }

  const data = stats || {}

  // Severity palette (Critical→High→Medium→Low) from theme tokens so the
  // distribution charts and priority dots stay correct in light + dark mode.
  const severityColors = [
    theme.palette.error.main,
    theme.palette.warning.main,
    theme.palette.info.main,
    theme.palette.success.main,
  ]
  const priorityDotColor = {
    critical: theme.palette.error.main,
    high: theme.palette.warning.main,
    medium: theme.palette.info.main,
    low: theme.palette.success.main,
  }

  // Asset health A–F grades (canonical terminology). Each row carries its letter
  // + full label + count + %, so meaning is never colour-only. Colours are
  // semantic theme tokens (good → needs-attention → non-functional).
  const assetGrades = [
    { key: 'A', label: 'Good, in use', color: 'success' },
    { key: 'B', label: 'Good, not in use', color: 'info' },
    { key: 'C', label: 'In use, needs repair', color: 'warning' },
    { key: 'D', label: 'In use, needs maintenance', color: 'warning' },
    { key: 'E', label: 'Not in use, needs repair & maintenance', color: 'error' },
    { key: 'F', label: 'Non-functional / to discard', color: 'error' },
  ]

  // Chart data for Work Orders by Priority
  const priorityChartData = {
    labels: ['Critical', 'High', 'Medium', 'Low'],
    datasets: [
      {
        data: [
          data.work_orders?.by_priority?.critical || 0,
          data.work_orders?.by_priority?.high || 0,
          data.work_orders?.by_priority?.medium || 0,
          data.work_orders?.by_priority?.low || 0,
        ],
        backgroundColor: severityColors,
        borderWidth: 0,
      },
    ],
  }

  // Chart data for Assets by Criticality
  const criticalityChartData = {
    labels: ['Critical', 'High', 'Medium', 'Low'],
    datasets: [
      {
        data: [
          data.assets?.by_criticality?.critical || 0,
          data.assets?.by_criticality?.high || 0,
          data.assets?.by_criticality?.medium || 0,
          data.assets?.by_criticality?.low || 0,
        ],
        backgroundColor: severityColors,
        borderWidth: 0,
      },
    ],
  }

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          boxWidth: 12,
          padding: 15,
          font: { size: 11 },
        },
      },
    },
  }

  // Alerts based on data
  const alerts = []
  if (data.work_orders?.overdue > 0) {
    alerts.push({ severity: 'error', message: `${data.work_orders.overdue} overdue work orders need attention` })
  }
  if (data.preventive_maintenance?.overdue > 0) {
    alerts.push({ severity: 'warning', message: `${data.preventive_maintenance.overdue} PM schedules are overdue` })
  }
  if (data.calibrations?.overdue > 0) {
    alerts.push({ severity: 'warning', message: `${data.calibrations.overdue} calibrations are overdue` })
  }
  if (data.inventory?.out_of_stock > 0) {
    alerts.push({ severity: 'error', message: `${data.inventory.out_of_stock} inventory items are out of stock` })
  }


  return (
    <Box>
      <PageHeader
        title={`Welcome back, ${user?.first_name ? `${user.first_name}${user.last_name ? ` ${user.last_name}` : ''}` : user?.username || 'User'}`}
        subtitle={`Overview of your maintenance operations${orgName ? ` · ${orgName}` : ''}`}
      />

      {/* Alerts Section */}
      {alerts.length > 0 && (
        <Box
          mb={3}
          display="flex"
          flexDirection="column"
          gap={1}
          role="region"
          aria-live="polite"
          aria-label="Operational alerts"
        >
          {alerts.map((alert, idx) => (
            <Alert key={idx} severity={alert.severity} variant="outlined">
              {alert.message}
            </Alert>
          ))}
        </Box>
      )}

      {/* KPI row — at-a-glance counts, each deep-linking to its records */}
      <Grid container spacing={{ xs: 2, md: 3 }} sx={{ mb: { xs: 2, md: 3 } }}>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Total Assets"
            value={data.assets?.total || 0}
            icon={<Inventory2 />}
            color="primary"
            onClick={() => navigate('/assets')}
            subtitle={`${data.assets?.by_status?.A || 0} good and in use`}
            sparkline={data.assets?.new_trend}
            trend={
              data.assets?.new_trend
                ? {
                    color: 'text.secondary',
                    text: `${data.assets?.new_last7 || 0} new · last 7 days`,
                  }
                : undefined
            }
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Open Work Orders"
            value={data.work_orders?.open || 0}
            icon={<Assignment />}
            color="info"
            onClick={() => navigate('/work-orders')}
            subtitle={`${data.work_orders?.created_this_month || 0} this month`}
            sparkline={data.work_orders?.created_trend}
            trend={
              data.work_orders?.created_trend
                ? {
                    color: 'text.secondary',
                    text: `${(data.work_orders.created_trend.slice(7).reduce((a, b) => a + b, 0))} created · last 7 days`,
                  }
                : undefined
            }
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Pending PM"
            value={data.preventive_maintenance?.pending || 0}
            icon={<Schedule />}
            color="warning"
            onClick={() => navigate('/pm-schedule')}
            subtitle={`${data.preventive_maintenance?.due_this_week || 0} due this week`}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Overdue Items"
            value={(data.work_orders?.overdue || 0) + (data.preventive_maintenance?.overdue || 0)}
            icon={<Warning />}
            color="error"
            onClick={() => navigate('/work-orders')}
            subtitle="Requires attention"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Low Stock Items"
            value={data.inventory?.low_stock || 0}
            icon={<LocalShipping />}
            color="warning"
            onClick={() => navigate('/inventory')}
            subtitle={`${data.inventory?.total_items || 0} total items`}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Calibrations Due"
            value={data.calibrations?.due_soon || 0}
            icon={<Speed />}
            color="info"
            onClick={() => navigate('/calibrations')}
            subtitle="Next 30 days"
          />
        </Grid>
      </Grid>

      {/* Compliance & health + asset-health breakdown — true ratio metrics as
          on-brand radial gauges, paired with the A–F grade mix so the band reads
          as a full, balanced row. Each deep-links to its records; a null ratio
          renders an honest "No data". */}
      <Grid container spacing={{ xs: 2, md: 3 }} sx={{ mb: { xs: 2, md: 3 } }}>
        <Grid item xs={12} md={5}>
          <Card title="Compliance & health" sx={{ height: '100%' }}>
            <Grid container spacing={{ xs: 1, md: 2 }} alignItems="center" sx={{ height: '100%' }}>
              <Grid item xs={6} sm={4}>
                <RadialGauge
                  value={data.preventive_maintenance?.compliance_rate}
                  label="PM compliance"
                  caption="completed on time, 180d"
                  color="primary"
                  onClick={() => navigate('/pm-schedule')}
                />
              </Grid>
              <Grid item xs={6} sm={4}>
                <RadialGauge
                  value={data.calibrations?.pass_rate}
                  label="Calibration pass rate"
                  caption={`${data.calibrations?.pass_total || 0} records, 180d`}
                  color="success"
                  onClick={() => navigate('/calibrations')}
                />
              </Grid>
              <Grid item xs={6} sm={4}>
                <RadialGauge
                  value={
                    data.assets?.total
                      ? ((data.assets?.by_status?.A || 0) + (data.assets?.by_status?.B || 0)) /
                          data.assets.total *
                        100
                      : null
                  }
                  label="Assets operational"
                  caption="grade A or B"
                  color="info"
                  onClick={() => navigate('/assets')}
                />
              </Grid>
            </Grid>
          </Card>
        </Grid>

        {/* Asset health — A–F grade breakdown as labelled horizontal bars. Honest
            counts from assets.by_status; never colour-only (letter + label + %). */}
        <Grid item xs={12} md={7}>
          <Card
            title="Asset health"
            sx={{ height: '100%' }}
            action={
              <Chip
                label="View assets"
                size="small"
                onClick={() => navigate('/assets')}
                sx={{ cursor: 'pointer' }}
              />
            }
          >
            <Box display="flex" flexDirection="column" gap={1.5}>
              {assetGrades.map((g) => {
                const count = data.assets?.by_status?.[g.key] || 0
                const total = data.assets?.total || 0
                const pct = total ? Math.round((count / total) * 100) : 0
                return (
                  <Box key={g.key}>
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      mb={0.5}
                    >
                      <Box display="flex" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
                        <Box
                          aria-hidden="true"
                          sx={{
                            width: 22,
                            height: 22,
                            flexShrink: 0,
                            borderRadius: 0.75,
                            bgcolor: `${g.color}.main`,
                            color: `${g.color}.contrastText`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 12,
                            fontWeight: 700,
                          }}
                        >
                          {g.key}
                        </Box>
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {g.label}
                        </Typography>
                      </Box>
                      <Typography variant="body2" fontWeight={600} sx={{ flexShrink: 0, ml: 1 }}>
                        {count} · {pct}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={pct}
                      color={g.color}
                      aria-label={`Grade ${g.key}, ${g.label}: ${count} assets, ${pct} percent`}
                      sx={{ height: 6, borderRadius: 3 }}
                    />
                  </Box>
                )
              })}
            </Box>
          </Card>
        </Grid>
      </Grid>

      {/* Overview distributions — lightweight, always-visible breakdowns kept with
          the health band so the charts read together, above the work area. */}
      <Grid container spacing={{ xs: 2, md: 3 }} sx={{ mb: { xs: 2, md: 3 } }}>
        <Grid item xs={12} md={4}>
          <Card title="Work Orders by Priority">
            <Box height={220}>
              <Doughnut data={priorityChartData} options={chartOptions} />
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card title="Assets by Criticality">
            <Box height={220}>
              <Doughnut data={criticalityChartData} options={chartOptions} />
            </Box>
          </Card>
        </Grid>

        {/* Assets by Category */}
        <Grid item xs={12} md={4}>
          <Card title="Top Asset Categories">
            {data.assets?.by_category?.length > 0 ? (
              <List dense disablePadding>
                {data.assets.by_category.map((cat, index) => (
                  <Box key={index}>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemText
                        primary={cat.category__name || 'Uncategorized'}
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                      <Box display="flex" alignItems="center" gap={2}>
                        <LinearProgress
                          variant="determinate"
                          value={(cat.count / data.assets.total) * 100}
                          sx={{ width: 100, height: 6, borderRadius: 3 }}
                        />
                        <Typography variant="body2" fontWeight={600} sx={{ minWidth: 30 }}>
                          {cat.count}
                        </Typography>
                      </Box>
                    </ListItem>
                    {index < data.assets.by_category.length - 1 && <Divider />}
                  </Box>
                ))}
              </List>
            ) : (
              <Box py={2} textAlign="center">
                <Typography color="text.secondary">No category data</Typography>
              </Box>
            )}
          </Card>
        </Grid>
      </Grid>

      {/* Performance analytics — expanded by default so the trend graphs are up
          front; still code-split (heavy Plotly loads lazily) and collapsible. */}
      <Box
        sx={{
          mb: { xs: 2, md: 3 },
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          overflow: 'hidden',
          bgcolor: 'background.paper',
        }}
      >
        <Box
          role="button"
          tabIndex={0}
          aria-expanded={analyticsOpen}
          aria-controls="dashboard-analytics-panel"
          onClick={toggleAnalytics}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              toggleAnalytics()
            }
          }}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            p: 2,
            cursor: 'pointer',
            '&:hover': { backgroundColor: 'action.hover' },
            '&:focus-visible': {
              outline: '2px solid',
              outlineColor: 'primary.main',
              outlineOffset: '-2px',
            },
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight={700}>
              Performance analytics
            </Typography>
            <Typography variant="body2" color="text.secondary">
              High-level maintenance performance and asset health
            </Typography>
          </Box>
          {analyticsOpen ? (
            <ExpandLess aria-hidden="true" color="action" />
          ) : (
            <ExpandMore aria-hidden="true" color="action" />
          )}
        </Box>
        <Collapse in={analyticsOpen} unmountOnExit={false}>
          <Box
            id="dashboard-analytics-panel"
            role="region"
            aria-label="Performance analytics"
            sx={{ px: { xs: 1.5, md: 2 }, pb: { xs: 1.5, md: 2 } }}
          >
            {analyticsMounted && (
              <Suspense fallback={<Skeleton variant="rounded" height={360} />}>
                <AnalyticsSection showTitle={false} />
              </Suspense>
            )}
          </Box>
        </Collapse>
      </Box>

      {/* Primary work area — the records the user acts on and the primary actions */}
      <Grid container spacing={{ xs: 2, md: 3 }} sx={{ mb: { xs: 2, md: 3 } }}>
        {/* Recent Work Orders */}
        <Grid item xs={12} md={8}>
          <Card
            title="Recent Work Orders"
            action={
              <Chip
                label="View All"
                size="small"
                onClick={() => navigate('/work-orders')}
                sx={{ cursor: 'pointer' }}
              />
            }
          >
            {data.work_orders?.recent?.length > 0 ? (
              <List dense disablePadding>
                {data.work_orders.recent.map((wo, index) => (
                  <Box key={wo.id}>
                    <ListItem
                      role="button"
                      tabIndex={0}
                      aria-label={`Work order ${wo.work_order_number}, ${wo.priority || 'low'} priority${wo.title ? `, ${wo.title}` : ''}`}
                      sx={{
                        px: 0,
                        cursor: 'pointer',
                        '&:hover': { backgroundColor: 'action.hover' },
                        borderRadius: 1,
                        '&:focus-visible': {
                          outline: '2px solid',
                          outlineColor: 'primary.main',
                          outlineOffset: '-2px',
                        },
                      }}
                      onClick={() => navigate(`/work-orders/${wo.id}`)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          navigate(`/work-orders/${wo.id}`)
                        }
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Circle
                          aria-hidden="true"
                          sx={{
                            fontSize: 12,
                            color: priorityDotColor[wo.priority] || priorityDotColor.low,
                          }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="body2" fontWeight={500}>
                              {wo.work_order_number}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" noWrap sx={{ flex: 1 }}>
                              {wo.title}
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                            <StatusChip status={wo.status} size="small" />
                            <Typography variant="caption" color="text.secondary">
                              {wo.asset__name || 'No asset'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatDateTime(wo.created_at) || '-'}
                            </Typography>
                          </Box>
                        }
                      />
                      <ArrowForward fontSize="small" color="action" />
                    </ListItem>
                    {index < data.work_orders.recent.length - 1 && <Divider />}
                  </Box>
                ))}
              </List>
            ) : (
              <Box py={4} textAlign="center">
                <Typography color="text.secondary">No recent work orders</Typography>
              </Box>
            )}
          </Card>
        </Grid>

        {/* Right Column */}
        <Grid item xs={12} md={4}>
          {/* Quick Actions — the primary actions taken from the dashboard */}
          <Card title="Quick Actions" sx={{ mb: 3 }}>
            <Box display="flex" flexDirection="column" gap={2}>
              <PermissionGate permission="assets.view">
                <Box
                  role="button"
                  tabIndex={0}
                  aria-label="Scan QR: scan an asset or vehicle QR code"
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: 'action.hover',
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: 'action.selected' },
                    '&:focus-visible': {
                      outline: '2px solid',
                      outlineColor: 'primary.main',
                      outlineOffset: 2,
                    },
                  }}
                  onClick={() => navigate('/assets/scan')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      navigate('/assets/scan')
                    }
                  }}
                >
                  <Box display="flex" alignItems="center" gap={2}>
                    <QrCodeScanner color="primary" />
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        Scan QR
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Scan an asset or vehicle QR code
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </PermissionGate>

              <PermissionGate permission="work_orders.create">
                <Box
                  role="button"
                  tabIndex={0}
                  aria-label="Create Work Order: log a new maintenance request"
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: 'action.hover',
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: 'action.selected' },
                    '&:focus-visible': {
                      outline: '2px solid',
                      outlineColor: 'primary.main',
                      outlineOffset: 2,
                    },
                  }}
                  onClick={() => navigate('/work-orders')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      navigate('/work-orders')
                    }
                  }}
                >
                  <Box display="flex" alignItems="center" gap={2}>
                    <Build color="primary" />
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        Create Work Order
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Log a new maintenance request
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </PermissionGate>

              <PermissionGate permission="assets.create">
                <Box
                  role="button"
                  tabIndex={0}
                  aria-label="Register Asset: add a new asset to the system"
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: 'action.hover',
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: 'action.selected' },
                    '&:focus-visible': {
                      outline: '2px solid',
                      outlineColor: 'primary.main',
                      outlineOffset: 2,
                    },
                  }}
                  onClick={() => navigate('/assets')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      navigate('/assets')
                    }
                  }}
                >
                  <Box display="flex" alignItems="center" gap={2}>
                    <Inventory2 color="primary" />
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        Register Asset
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Add a new asset to the system
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </PermissionGate>

              <PermissionGate permission="reports.view">
                <Box
                  role="button"
                  tabIndex={0}
                  aria-label="View Reports: analytics and insights"
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: 'action.hover',
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: 'action.selected' },
                    '&:focus-visible': {
                      outline: '2px solid',
                      outlineColor: 'primary.main',
                      outlineOffset: 2,
                    },
                  }}
                  onClick={() => navigate('/reports')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      navigate('/reports')
                    }
                  }}
                >
                  <Box display="flex" alignItems="center" gap={2}>
                    <TrendingUp color="primary" />
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        View Reports
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Analytics and insights
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </PermissionGate>
            </Box>
          </Card>

          {/* Low Stock Items */}
          <Card title="Low Stock Items">
            {data.inventory?.low_stock_list?.length > 0 ? (
              <List dense disablePadding>
                {data.inventory.low_stock_list.map((item, index) => (
                  <Box key={item.id}>
                    <ListItem
                      role="button"
                      tabIndex={0}
                      aria-label={`${item.name}, part number ${item.part_number}, ${item.current_stock} of ${item.reorder_point} in stock`}
                      sx={{
                        px: 0,
                        cursor: 'pointer',
                        borderRadius: 1,
                        '&:hover': { backgroundColor: 'action.hover' },
                        '&:focus-visible': {
                          outline: '2px solid',
                          outlineColor: 'primary.main',
                          outlineOffset: '-2px',
                        },
                      }}
                      onClick={() => navigate('/inventory')}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          navigate('/inventory')
                        }
                      }}
                    >
                      <ListItemText
                        primary={item.name}
                        secondary={`Part #: ${item.part_number}`}
                        primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                      <Box textAlign="right">
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          color={item.current_stock === 0 ? 'error.main' : 'warning.main'}
                        >
                          {item.current_stock} / {item.reorder_point}
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={Math.min((item.current_stock / item.reorder_point) * 100, 100)}
                          color={item.current_stock === 0 ? 'error' : 'warning'}
                          sx={{ width: 60, height: 4, borderRadius: 2, mt: 0.5 }}
                        />
                      </Box>
                    </ListItem>
                    {index < data.inventory.low_stock_list.length - 1 && <Divider />}
                  </Box>
                ))}
              </List>
            ) : (
              <Box py={2} textAlign="center">
                <CheckCircle color="success" sx={{ fontSize: 32, mb: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  All stock levels are healthy
                </Typography>
              </Box>
            )}
          </Card>
        </Grid>
      </Grid>
    </Box>
  )
}

export default Dashboard
